import './gate.css';
import { GATE, isBoss, type EnemyClass, type Sim, type SimEvent } from '@orb/sim';
import type { SimView } from '../game/Driver';

/**
 * The gate's plate (HUD v2): its strength as GATE.breaches cells, the one a breach takes cracking and
 * going dark, a line under it («Бігун прорвався · брама −1»), and from three left on, the gate
 * breaking — the plate burns red, the line stays («Брама ламається · ще 2 прориви»), the screen's
 * edges redden. In co-op the gate is shared.
 */
const CRITICAL = 3;

/** 1 прорив · 2 прориви · 5 проривів. */
export function breaches(n: number): string {
  const a = n % 100, b = n % 10;
  return a >= 11 && a <= 14 ? 'проривів' : b === 1 ? 'прорив' : b >= 2 && b <= 4 ? 'прориви' : 'проривів';
}
const who = (cls: EnemyClass) => (cls === 'runner' ? 'Бігун прорвався' : isBoss(cls) ? 'Бос прорвався' : 'Ворог прорвався');

export class GatePlate implements SimView {
  readonly el: HTMLDivElement;
  private readonly count: HTMLElement;
  private readonly cells: HTMLElement[];
  private readonly note: HTMLDivElement;
  private readonly vignette: HTMLDivElement;
  private left = GATE.breaches;
  private noteTimer = 0;

  private readonly meta: HTMLElement;
  private metaKey = '';

  constructor(host: HTMLElement, private readonly sim: Sim, private readonly o: { coop: boolean; wide: boolean; seat: number }) {
    const d = document;
    this.el = d.createElement('div');
    this.el.className = 'gate';
    this.el.setAttribute('role', 'status');
    this.el.innerHTML = `<div class="gate-plate"><div class="gate-head"><span class="gate-label">${o.coop ? 'Брама · спільна' : 'Брама'}</span><span class="gate-count"><b></b> / ${GATE.breaches}</span></div><div class="gate-cells">${'<i></i>'.repeat(GATE.breaches)}</div><div class="gate-meta"></div></div><div class="gate-note" aria-live="polite"></div>`;
    this.count = this.el.querySelector('.gate-count b')!;
    this.cells = [...this.el.querySelectorAll<HTMLElement>('.gate-cells i')];
    this.note = this.el.querySelector('.gate-note')!;
    this.meta = this.el.querySelector('.gate-meta')!;
    this.vignette = d.createElement('div');
    this.vignette.className = 'gate-vignette';
    this.vignette.setAttribute('aria-hidden', 'true');
    host.append(this.vignette);
    // phones: in the corner row beside ☰ (as the design has it); wide screens: centred on top
    const bar = host.querySelector('.menu-bar');
    if (bar && !o.wide) bar.firstElementChild ? bar.firstElementChild.after(this.el) : bar.append(this.el);
    else { this.el.classList.add('top'); host.append(this.el); }
    this.show(false);
  }

  events(events: readonly SimEvent[]) {
    for (const e of events) if (e.type === 'leak') this.breach(e.cls);
  }

  /** The match so far under the cells: its time and our kills (written once a second at most). */
  tick() {
    const st = this.sim.state, s = Math.floor(st.clock), kills = st.stats.kills[this.o.seat] ?? 0, key = `${s},${kills}`;
    if (key === this.metaKey) return;
    this.metaKey = key;
    this.meta.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} · ${kills.toLocaleString('uk-UA')} ${kills % 10 === 1 && kills % 100 !== 11 ? 'вбивство' : kills % 10 >= 2 && kills % 10 <= 4 && (kills % 100 < 12 || kills % 100 > 14) ? 'вбивства' : 'вбивств'}`;
  }

  /** A quiet catch-up or a snapshot: take the count as it is, without the drama. */
  resync() { this.show(false); }

  private breach(cls: EnemyClass) {
    this.show(true);
    if (this.left <= 0) return; // «Брама впала»: the summary says it
    this.say(`${who(cls)} · брама −1`, 'hit');
    clearTimeout(this.noteTimer);
    this.noteTimer = window.setTimeout(() => this.settle(), 2400);
  }

  private show(animate: boolean) {
    const left = Math.max(0, GATE.breaches - this.sim.state.stats.leaks);
    const lost = left < this.left;
    this.left = left;
    this.count.textContent = String(left);
    this.cells.forEach((c, i) => {
      const was = c.dataset.on === '1', on = i < left;
      c.dataset.on = on ? '1' : '0';
      // the cell a breach took cracks first, then goes dark
      if (animate && was && !on) { c.classList.remove('crack'); void c.offsetWidth; c.classList.add('crack'); }
    });
    const critical = left > 0 && left <= CRITICAL;
    this.el.toggleAttribute('data-critical', critical);
    this.el.toggleAttribute('data-fallen', left === 0);
    this.vignette.toggleAttribute('data-on', critical || left === 0);
    if (animate && lost) { this.el.classList.remove('shake'); void this.el.offsetWidth; this.el.classList.add('shake'); }
    if (!animate) this.settle();
  }

  /** What stays under the plate between breaches: nothing, or the gate breaking. */
  private settle() {
    if (this.left > 0 && this.left <= CRITICAL) this.say(`Брама ламається · ще ${this.left} ${breaches(this.left)}`, 'critical');
    else this.say('', '');
  }

  private say(text: string, tone: '' | 'hit' | 'critical') {
    if (this.note.textContent === text) return;
    this.note.textContent = text;
    this.note.dataset.tone = tone;
    this.note.classList.remove('in'); void this.note.offsetWidth;
    if (text) this.note.classList.add('in');
  }
}
